import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';

interface SurveyDialogProps {
  open: boolean;
  title: string;
  message: string;
  onOpenChange: (open: boolean) => void;
  /** 에러 모드: 카운트다운 후 자동 리다이렉트 */
  isError?: boolean;
  /** 카운트다운 초 (기본값: 3) */
  countdownSeconds?: number;
  /** 카운트다운 완료 시 호출 */
  onCountdownEnd?: () => void;
  /** 텍스트 정렬 (기본값: center) */
  textAlign?: 'left' | 'center';
  /** 번호 리스트 아이템 (들여쓰기 적용) */
  listItems?: string[];
}

const SurveyDialog = ({
  open,
  title,
  message,
  onOpenChange,
  isError = false,
  countdownSeconds = 3,
  onCountdownEnd,
  textAlign = 'center',
  listItems,
}: SurveyDialogProps) => {
  const [countdown, setCountdown] = useState(countdownSeconds);

  useEffect(() => {
    if (!open || !isError) {
      setCountdown(countdownSeconds);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onCountdownEnd?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, isError, countdownSeconds, onCountdownEnd]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent noCloseBtn className="w-[320px] p-[24px]">
        <div className={`flex flex-col gap-4 ${textAlign === 'left' ? 'items-start' : 'items-center'}`}>
          {title && <h2 className={`text-base font-semibold text-gray-900 ${textAlign === 'left' ? 'text-left' : 'text-center'}`}>{title}</h2>}
          <p className={`whitespace-pre-wrap text-sm text-gray-700 ${textAlign === 'left' ? 'text-left' : 'text-center'}`}>{message}</p>
          {listItems && listItems.length > 0 && (
            <ol className="w-full list-none space-y-1 text-left text-sm text-gray-700">
              {listItems.map((item, idx) => (
                <li key={idx} className="flex">
                  <span className="shrink-0">{idx + 1}.&nbsp;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ol>
          )}

          {isError ? (
            <div className="flex w-full flex-col items-center gap-3 mt-2">
              {/* 로딩 스피너 */}
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
              <p className="text-sm text-gray-500">{countdown}초 후 메인페이지로 이동합니다</p>
            </div>
          ) : (
            <div className="mt-2 w-full">
              <Button
                size="small"
                variant="solid"
                type="button"
                className="w-full py-[11px]"
                onClick={() => onOpenChange(false)}
              >
                확인
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SurveyDialog;

